import { useRef } from 'react';
import back from '../../../../../../assets/icons/ic_caret_left.svg';
import { useDictionary } from '../../../../../../utilities/hooks/UseDictionary';
import SimpleLoader from '../../../../common/SimpleLoader';
import * as common from '../../../../common/__redux/commonRedux';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { NavigatorPaths } from '../../../../../../assets/dictionaries/NavigatorPaths';
import {
	getExperienceByProvider,
	getProviderDetails,
	getMarketplaceExperienceByProvider,
	getResaleCollectionByProvider,
	getResellers,
	getProviderBookingList,
	updateCommission,
	updateSubscription
} from '../networking';
import Experiences from './tabs/experiences/Experiences';
import ProviderDetailRight from './ProviderDetailRight';
import Assets from './tabs/experiences/Assets';
import { AmountFormatHelper } from '../../../../../../utilities/helpers/AmountFormatHelper';
import Resellers from './tabs/resellers/Resellers';
import BookingsTab from './tabs/booking/bookings';
import useClientWidth from '../../../../../../utilities/hooks/useClientWidth';
import { UseCurrencyDecorator } from '../../../../../../utilities/hooks/UseCurrencyDecorator';
import ProfileImage from '../../../../common/ProfileImage';
import ProviderGttd from './tabs/ProviderGtdd';
import ProviderDocuments from './tabs/documents/ProviderDocuments';
import { ToastContainer } from 'react-toastify';
import GenericProviderFieldDialog from '../GenericProviderFieldDialog';
import ConfirmationModal from '../../../../common/ConfirmationModal';

const ProviderDetail = ({ id, dismissDetails, setErrorMessage }) => {
	const dictionary = useDictionary();
	const clientWidth = useClientWidth();
	const currencyDecorator = UseCurrencyDecorator();
	const history = useHistory();
	const [tabIndex, setTabIndex] = useState(0);
	const [opacity, setOpacity] = useState(0);
	const [showLoader, setLoaderVisibility] = useState(true);
	const [details, setDetails] = useState(null);
	const [experiences, setExperiences] = useState([]);
	const [resellers, setResellers] = useState([]);
	const [marketplaceExperiences, setMarketplaceExperiences] = useState([]);
	const [resaleCollection, setResaleCollection] = useState([]);
	const [assets, setAssets] = useState([]);
	const [providerGttd, setProviderGttd] = useState(null);
	const [showCommissionDialog, setCommissionVisibility] = useState(false);
	const [showSubscriptionDialog, setSubscriptionVisibility] = useState(false);
	// Target plan for the subscription dialog: 'free' | 'business' | 'enterprise'.
	const [targetPlan, setTargetPlan] = useState(null);
	const [upiriaCommission, setUpiriaCommission] = useState(0);
	const [placeId, setPlaceId] = useState(null);
	const [bookingList, setbookingList] = useState([]);
	const [loading, setLoading] = useState(false);
	const [hasMore, setHasMore] = useState(true);
	const [totalBooking, setTotalBooking] = useState(0);
	const [pageNumber, setPageNumber] = useState(0);
	const pageSize = 15;
	const isFetchingRef = useRef(false);
	const isSubscription =
		details?.is_subscription === true ||
		details?.is_subscription === 1 ||
		details?.is_subscription === '1';
	// Plan name from the provider payload; legacy fallback maps the
	// is_subscription boolean (subscribed-without-plan = business).
	const planName = details?.plan ?? (isSubscription ? 'business' : 'free');
	const isEnterprise = planName === 'enterprise';
	const currentPlan = isEnterprise
		? dictionary.enterprise_plan
		: isSubscription
			? dictionary.business_plan
			: dictionary.basic;

	useEffect(() => {
		setOpacity(1);
		getDetailsByIdAsync();
	}, [id]); // Runs only when `id` changes, prevents unnecessary calls

	useEffect(() => {
		setbookingList([]);
		setPageNumber(0);
		setTotalBooking(0);
		setHasMore(true);
		fetchMoreData(true);
	}, [id]); // Ensures it runs only when `id` changes

	const applyProviderData = (data) => {
		setUpiriaCommission(
			data?.upiria_commission
				? AmountFormatHelper.formatAmount(data.upiria_commission)
				: 0
		);
		setPlaceId(data?.place_id);
		setDetails(data);
		setProviderGttd(data.gttd || 'No data available');
	};

	const fetchProviderDetailsOnly = async () => {
		const { data } = await getProviderDetails(id);
		applyProviderData(data);
	};

	const refreshProviderDetailsOnly = async () => {
		try {
			await fetchProviderDetailsOnly();
		} catch (e) {
			setErrorMessage(e.response?.data?.error_code?.toString() || 'Unknown Error');
		}
	};

	const getDetailsByIdAsync = async () => {
		if (isFetchingRef.current) return; // Prevent duplicate calls
		isFetchingRef.current = true; // Lock request

		try {
			let { data } = await getProviderDetails(id);
			if (!data) throw new Error('No provider details returned');

			// Update state with provider details
			applyProviderData(data);

			// Fetch extra data concurrently (faster)
			const [
				experienceResponse,
				marketplaceResponse,
				resaleResponse,
				resellersResponse
			] = await Promise.all([
				getExperienceByProvider(data.id),
				getMarketplaceExperienceByProvider(data.id),
				getResaleCollectionByProvider(data.id),
				getResellers(data.id)
			]);

			// Extract and set state
			setExperiences(experienceResponse?.data ?? []);
			setMarketplaceExperiences(marketplaceResponse?.data ?? []);
			setResaleCollection(resaleResponse?.data ?? []);
			setResellers(resellersResponse?.data ?? []);

			// Process assets separately
			const experienceAssets = [];
			(experienceResponse?.data ?? []).forEach(experience => {
				(experience.assets ?? []).forEach(asset => {
					experienceAssets.push(asset);
				});
			});
			setAssets(experienceAssets);

		} catch (e) {
			console.error('Error fetching provider details:', e);
			setErrorMessage(e.response?.data?.error_code?.toString() || 'Unknown Error');
		} finally {
			setLoaderVisibility(false);
			isFetchingRef.current = false; // Unlock request
		}
	};

	const onBookingClicked = (booking) => {
		history.push(`admin/bookings/${booking.id}`, booking);
	};

	const fetchMoreData = async (reset = false) => {
		const nextPageNumber = reset ? 0 : pageNumber;
		const currentBookingList = reset ? [] : bookingList;

		try {
			if (
				!reset &&
				totalBooking &&
				(totalBooking <= pageSize || currentBookingList.length === totalBooking)
			) {
				setHasMore(false);
				return;
			}

			setLoading(true);
			const booking = await getProviderBookingList(id, pageSize, nextPageNumber);
			setTotalBooking(booking?.data?.recordsFiltered);

			const bookingLists = booking?.data?.data ?? [];

			if (bookingLists.length === 0) {
				setHasMore(false);
			} else {
				setbookingList(prev =>
					reset ? bookingLists : [...prev, ...bookingLists]
				);
				setPageNumber(nextPageNumber + 1);
			}
		} catch (error) {
			console.error('Error fetching data:', error);
		} finally {
			setLoading(false);
		}
	};

	const routeMap = {
		0: <Experiences experiences={experiences} />,
		1: <Experiences experiences={marketplaceExperiences} />,
		2: <Experiences experiences={resaleCollection} />,
		3: <Resellers providers={resellers} />,
		4: <Assets assets={assets} />,
		5: (
			<BookingsTab
				details={bookingList}
				onBookingClicked={onBookingClicked}
				loading={loading}
				hasMore={hasMore}
				onLoadMore={fetchMoreData}
			/>
		),
		6: (
			<ProviderGttd
				gttd={providerGttd}
				providerId={id}
				placeId={placeId}
				isMatched={details?.is_matched}
				onSuccess={refreshProviderDetailsOnly}
			/>
		),
		7: (
			<ProviderDocuments
				files={details?.files ?? []}
				onUpdated={refreshProviderDetailsOnly}
			/>
		)
	};

	const dismiss = () => {
		dismissDetails();
		setOpacity(0);
		history.push(NavigatorPaths.applications.path);
	};

	const showPopupSetCommission = () => {
		setCommissionVisibility(true);
	};

	const showPopupSetSubscription = (plan) => {
		setTargetPlan(plan);
		setSubscriptionVisibility(true);
	};

	const handleUpiriaCommissionUpdate = async (newCommissionValue) => {
		await updateCommission(id, newCommissionValue); // Wait for the API call
		if (refreshProviderDetailsOnly) {
			await refreshProviderDetailsOnly(); // Wait for the parent to re-fetch and update state
		}
		setCommissionVisibility(false); // Close the dialog
	};

	const handleSubscriptionUpdate = async (onSuccess, onError) => {
		try {
			await updateSubscription(
				id,
				targetPlan === 'free' ? 0 : 1,
				targetPlan === 'free' ? undefined : targetPlan
			);
			await fetchProviderDetailsOnly();
			onSuccess();
		} catch (error) {
			console.error('Error updating subscription:', error);
			onError();
		}
	};

	return (
		<div className="booking-details-wrapper provider-booking-details" style={{ opacity: opacity }}>
			<div className="booking-header-container booking-header-container-details provider-header-section-wrapper">
				<div className="d-flex justify-between w-60">
					<div className="booking-header-title">
						<div className="booking-detail-back-button" onClick={() => dismiss()}>
							<img src={back} />
						</div>
						<div className="d-flex justify-center">
							<ProfileImage
								photo={details?.company_photo?.path}
								name={details?.brand_name}
								size={{ width: 32, height: 32, fontSize: 13, lineHeight: 16 }}
							/>
							<h2
								style={{ marginLeft: '8px' }}
								className="booking-details-header-text truncate"
								title={details?.brand_name}
								data-testid="booking-details-header"
							>
								{ details?.brand_name }
							</h2>
						</div>
					</div>
					{details ? (
						<div
							className="booking-details-header-text-wrapper"
							style={{
								display: 'flex',
								flexDirection: 'column',
								gap: 2,
								textAlign: 'right'
							}}
						>
							<div
								style={{
									display: 'flex',
									flexDirection: clientWidth > 1200 ? 'row' : 'column',
									justifyContent: 'flex-end',
									gap: clientWidth > 1200 ? 24 : 2
								}}
							>
								<h2
									className="booking-details-header-text"
									style={{ fontWeight: 'normal' }}
								>
									{dictionary.current_plan} : <span>{currentPlan}</span>
								</h2>
								<h2
									className="booking-details-header-text"
									style={{ fontWeight: 'normal' }}
								>
									{dictionary.available_balance} : <span>{currencyDecorator(AmountFormatHelper.formatAmount(details?.available_balance ?? 0), details?.currency)}</span>
								</h2>
							</div>
							<div
								style={{
									display: 'flex',
									flexDirection: clientWidth > 1200 ? 'row' : 'column',
									justifyContent: 'flex-end',
									gap: clientWidth > 1200 ? 24 : 2
								}}
							>
								<h2
									className="booking-details-header-text"
									style={{ fontWeight: 'normal' }}
								>
									{dictionary.upiria_commission} : <span>{AmountFormatHelper.formatAmount(details?.upiria_commission ?? 0)}%</span>
								</h2>

								<h2
									className="booking-details-header-text"
									style={{ fontWeight: 'normal' }}
								>
									{dictionary.pending_balance} : <span>{currencyDecorator(AmountFormatHelper.formatAmount(details?.pending_balance ?? 0), details?.currency)}</span>
								</h2>
							</div>
						</div>
					) : null}
				</div>
				<div className="experiences-actions">
					{details ? (
						<>
							<div className="experience-create-button">
								<div
									className="button"
									onClick={showPopupSetCommission}
									data-testid="set-upiria-commission"
								>
									{dictionary.set_upiria_commission}
								</div>
							</div>
							<div className="experience-create-button">
								<div
									className="button"
									onClick={() =>
										showPopupSetSubscription(
											isSubscription ? 'free' : 'business'
										)
									}
									data-testid="set-provider-subscription"
								>
									{isSubscription
										? dictionary.set_basic_plan
										: dictionary.set_business_plan}
								</div>
							</div>
							<div className="experience-create-button">
								<div
									className="button"
									onClick={() =>
										showPopupSetSubscription(
											isEnterprise ? 'business' : 'enterprise'
										)
									}
									data-testid="set-provider-enterprise"
								>
									{isEnterprise
										? dictionary.set_business_plan
										: dictionary.set_enterprise_plan}
								</div>
							</div>
						</>
					) : null}
				</div>
			</div>

			<div className="booking-details-body provider-details-body" style={{ height: clientWidth >= 986 ? '0' : '600px' }}>
				<div className="booking-details-container">
					<div className="settings-tab-bar booking-list-provider-table">
						<div
							onClick={() => setTabIndex(0)}
							className={
								tabIndex === 0
									? 'settings-tab settings-tab-active'
									: 'settings-tab'
							}
							data-testid="experiences-and-addons-tab"
						>
							{dictionary.experiences}({experiences.length > 0 ? experiences.length : 0})
						</div>
						<div
							onClick={() => setTabIndex(1)}
							className={
								tabIndex === 1 ? 'settings-tab settings-tab-active' : 'settings-tab'
							}
							data-testid="marketplaces-tab"
						>
							{dictionary.marketplaces}({marketplaceExperiences.length > 0 ? marketplaceExperiences.length : 0})
						</div>
						<div
							onClick={() => setTabIndex(2)}
							className={
								tabIndex === 2 ? 'settings-tab settings-tab-active' : 'settings-tab'
							}
							data-testid="resale_collection-tab"
						>
							{dictionary.resale_experience}({resaleCollection.length > 0 ? resaleCollection.length : 0})
						</div>
						<div
							onClick={() => setTabIndex(3)}
							className={
								tabIndex === 3 ? 'settings-tab settings-tab-active' : 'settings-tab'
							}
							data-testid="resellers-tab"
						>
							{dictionary.resellers}({resellers.length > 0 ? resellers.length : 0})
						</div>
						<div
							onClick={() => setTabIndex(5)}
							className={
								tabIndex === 5 ? 'settings-tab settings-tab-active' : 'settings-tab'
							}
							data-testid="provider-booking-tab"
						>
							{dictionary.bookings}({totalBooking > 0 ? totalBooking : 0})
						</div>
						<div
							onClick={() => setTabIndex(4)}
							className={
								tabIndex === 4 ? 'settings-tab settings-tab-active' : 'settings-tab'
							}
							data-testid="assets-tab"
						>
							{dictionary.assets}({assets.length > 0 ? assets.length : 0})
						</div>
						<div
							onClick={() => setTabIndex(6)}
							className={
								tabIndex === 6 ? 'settings-tab settings-tab-active' : 'settings-tab'
							}
							data-testid="gttd-tab"
						>
							{dictionary.gttd}
						</div>
						<div
							onClick={() => setTabIndex(7)}
							className={
								tabIndex === 7 ? 'settings-tab settings-tab-active' : 'settings-tab'
							}
							data-testid="documents-tab"
						>
							{dictionary.documents}({details?.files?.length > 0 ? details.files.length : 0})
						</div>
					</div>
					{details ? routeMap[tabIndex] : null}
					<SimpleLoader showLoader={showLoader} />
				</div>
				{details ? <ProviderDetailRight details={details} /> : null}
			</div>

			{showCommissionDialog ? (
				<GenericProviderFieldDialog
					dialogTitle={dictionary.set_upiria_commission}
					initialValue={upiriaCommission}
					label="Commission"
					onUpdate={handleUpiriaCommissionUpdate}
					setDialogVisibility={setCommissionVisibility}
				/>
			) : null}
			{showSubscriptionDialog ? (
				<ConfirmationModal
					setShowModal={setSubscriptionVisibility}
					onActionFinished={() => {}}
					asyncTask={handleSubscriptionUpdate}
					title={
						targetPlan === 'free'
							? dictionary.set_basic_plan
							: targetPlan === 'enterprise'
								? dictionary.set_enterprise_plan
								: dictionary.set_business_plan
					}
					message={
						targetPlan === 'free'
							? dictionary.deactivate_business_plan_confirmation_message
							: targetPlan === 'enterprise'
								? dictionary.activate_enterprise_plan_confirmation_message
								: dictionary.activate_business_plan_confirmation_message
					}
					successMessage={
						targetPlan === 'free'
							? dictionary.business_plan_deactivated
							: targetPlan === 'enterprise'
								? dictionary.enterprise_plan_activated
								: dictionary.business_plan_activated
					}
					errorMessage={dictionary.problem_updating_business_plan}
					action={
						targetPlan === 'free'
							? dictionary.set_basic_plan
							: targetPlan === 'enterprise'
								? dictionary.set_enterprise_plan
								: dictionary.set_business_plan
					}
				/>
			) : null}
			<ToastContainer />
		</div>
	);
};

ProviderDetail.propTypes = {
	id: PropTypes.string.isRequired,
	dismissDetails: PropTypes.func.isRequired,
	setErrorMessage: PropTypes.func.isRequired
};

export default connect(common.states, common.actions)(ProviderDetail);
